import {
  IonContent,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
} from '@ionic/react';
import {
  ellipsisVertical,
  informationCircleOutline,
  timeOutline,
} from 'ionicons/icons';
import { ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { useHasPermission } from '../../../../api/authorization.provider';
import {
  Scratchpad,
  ScratchpadBucketType,
} from '../../../../api/graphql/generated';
import {
  ActionButton,
  ActionButtonRef,
} from '../../../../components/button/action-button';
import { BackButtonProvider } from '../../../../components/header/back-button';
import { NestedPageHeader } from '../../../../components/header/page.header';
import { welderKeyOn } from '../../../../components/icons/welder-key-on';
import { ScratchpadPlaylistOptions } from '../../../../components/machine/playlist/scratchpad-playlist-options';
import { ScratchpadHistoryModal } from '../../../../components/scratchpad/scratchpad-history-modal';
import i18n from '../../../../i18n';
import { getLocalizedText } from '../../../../i18n/i18n.utils';
import {
  useIsMachineActive,
  useMachineContext,
} from '../../../../pages/machine/machine-context-provider';
import { useBeaconRouter } from '../../../../route/route.hooks';
import { DefaultClipboardService } from '../../../../service/ClipboardService';
import { logger } from '../../../../util/logger';
import { usePreventScreenLock } from '../../../../util/screen-lock.hooks';
import { ScratchpadPageProps } from '../../../app-slugs';
import { FlowActionBar } from '../../shared/component/action/flow-action-bar';
import { FlowComponent } from '../../shared/component/flow/flow';
import {
  FlowRefContext,
  useCreateFlowRef,
} from '../../shared/component/flow/flow-ref';
import { FlowSections } from '../../shared/component/flow/flow-sections';
import {
  ApplicationSettingsModalProvider,
  ApplicationSettingsModalRef,
} from '../../shared/component/settings/application-settings-modal-provider';
import { AppPageTitle } from '../../shared/component/title/app-page-title';
import { ClipboardContextProvider } from '../../shared/hook/clipboard.hooks';
import {
  FlowServiceContextFactoryCallback,
  FlowServiceContextFactoryOptions,
  FlowServiceContextProvider,
  useFlowServices,
} from '../../shared/hook/flow-service.hooks';
import { WaypointCaptureContextProvider } from '../../shared/hook/waypoint-capture.hooks';
import {
  Flow,
  FlowModal,
  FlowValidateError,
  FlowValidateResponse,
} from '../../shared/model/flow.types';
import { FlowAnalyticService } from '../../shared/service/flow-analytic.service';
import { FlowRuleServiceImpl } from '../../shared/service/flow-rule.service';
import { useHasBeaconCuttingFieldPermission } from '../../shared/util/permission';
import { CuttingEditActions } from './component/action/cutting-edit-actions';
import { CuttingFlowItemComponent } from './component/flow-item/cutting-flow-item';
import { CuttingFlowItemEdit } from './component/flow-item/cutting-flow-item-edit';
import { CuttingFlowItemMultiEdit } from './component/flow-item/cutting-flow-item-multi-edit';
import { CuttingPartDetail } from './component/part-detail/cutting-part-detail';
import { CuttingPartDetailModal } from './component/part-detail/cutting-part-detail-modal';
import { CutApplicationSettingsBaseFormTabbed } from './component/settings/cut-application-settings-base-form-tabbed';
import { CuttingTeachDetail } from './component/teach/cutting-teach-detail';
import { BeaconCuttingData } from './model/beacon-cutting-data';
import { CuttingFlowItemServiceImpl } from './service/cutting-flow-item.service';
import { CuttingFlowServiceImpl } from './service/cutting-flow.service';

const clipboardService = new DefaultClipboardService();

export interface FlowSaveResult {
  scratchpad: Scratchpad | undefined;
  errors: FlowValidateResponse;
}

const BeaconCuttingPage = (props: ScratchpadPageProps): ReactElement => {
  const { scratchpad } = props;
  const { machine } = useMachineContext();

  const flowServiceContextFactory: FlowServiceContextFactoryCallback<BeaconCuttingData.FlowConfig> =
    useCallback(
      (options: FlowServiceContextFactoryOptions) => {
        const { license, scratchpad } = options;

        const cutter = (license.licenseFormValues as BeaconCuttingData.License)
          .cutter;

        const flowItemService = new CuttingFlowItemServiceImpl(
          BeaconCuttingData.FlowItemsByCutter[
            cutter as BeaconCuttingData.Cutter
          ]
        );

        const flowRuleService = new FlowRuleServiceImpl();
        const flowService = new CuttingFlowServiceImpl(
          license,
          flowItemService,
          flowRuleService,
          BeaconCuttingData.Tools,
          machine
        );

        const appData = scratchpad.appData as Flow;
        if (appData?.defaultParameterSet) {
          flowItemService.setApplicationDefaultParameterSet(
            BeaconCuttingData.FlowItemRef.CUT,
            appData.defaultParameterSet
          );
        }

        return {
          appSlug: BeaconCuttingData.APP_SLUG,
          flowService,
          flowAnalyticService: new FlowAnalyticService(
            BeaconCuttingData.ANALYTICS_PREFIX
          ),
          flowItemService,
        };
      },
      [machine]
    );

  if (scratchpad?.id) {
    return (
      <ClipboardContextProvider clipboardService={clipboardService}>
        <FlowServiceContextProvider contextFactory={flowServiceContextFactory}>
          {({ flow }) => {
            const config = flow.config as BeaconCuttingData.FlowConfig;

            return (
              <WaypointCaptureContextProvider
                ioMapping={config.ioMapping}
                toolConfig={config.torch}
              >
                <BeaconCuttingPageExisting {...props} />
              </WaypointCaptureContextProvider>
            );
          }}
        </FlowServiceContextProvider>
      </ClipboardContextProvider>
    );
  } else {
    return (
      <ClipboardContextProvider clipboardService={clipboardService}>
        <FlowServiceContextProvider contextFactory={flowServiceContextFactory}>
          {({ flow }) => {
            const config = flow.config as BeaconCuttingData.FlowConfig;

            return (
              <WaypointCaptureContextProvider
                ioMapping={config.ioMapping}
                toolConfig={config.torch}
              >
                <BeaconCuttingPageNew {...props} />
              </WaypointCaptureContextProvider>
            );
          }}
        </FlowServiceContextProvider>
      </ClipboardContextProvider>
    );
  }
};

const BeaconCuttingPageNew = (props: ScratchpadPageProps) => {
  const { teamId, machineId } = props;
  const { builder } = useBeaconRouter();
  const {
    replace,
    location: { state },
  } = useHistory();

  const afterSave = (scratchpad: Scratchpad): void => {
    replace(
      builder.getMachineScratchpadRoute(teamId, machineId, scratchpad.id),
      state
    );
  };

  return (
    <BackButtonProvider>
      <IonPage>
        <NestedPageHeader
          title={i18n.t('common:scratchpad_create_part')}
          defaultBackRoute={builder.getMachineRoute(teamId, machineId)}
        />
        <IonContent>
          <CuttingPartDetail afterSave={afterSave} />
        </IonContent>
      </IonPage>
    </BackButtonProvider>
  );
};

const BeaconCuttingPageExisting = (props: ScratchpadPageProps) => {
  const { teamId, machineId, scratchpad } = props;

  const [activeModal, setActiveModal] = useState<FlowModal | undefined>();

  const { builder } = useBeaconRouter();
  const pageRef = useRef<HTMLDivElement>();
  const actionButtonRef = useRef<ActionButtonRef>();

  const { flow, saveFlow, flowService } =
    useFlowServices<BeaconCuttingData.FlowConfig>();
  const isMachineActive = useIsMachineActive();

  const [flowErrors, setFlowErrors] = useState<
    FlowValidateError[] | undefined
  >();

  usePreventScreenLock();

  const canUpdateScratchpad = useHasPermission(
    'update',
    'scratchpad',
    scratchpad
  );

  const canUpdateAllBeaconCuttingFields = useHasBeaconCuttingFieldPermission(
    scratchpad?.machineId,
    '*'
  );

  const onModalChange = (modal: FlowModal | undefined) => {
    setActiveModal(modal);
    actionButtonRef.current?.dismiss();
  };

  const save = useCallback(
    async (flow: Flow): Promise<FlowSaveResult> => {
      const errors = await flowService.validate(flow);

      const hasErrors = errors && errors.length > 0;
      let response: Scratchpad | undefined;

      if (!hasErrors) {
        const t0 = performance.now();
        response = await saveFlow(flow);
        const t1 = performance.now();
        logger.info(`saved flow completed in ${Math.floor(t1 - t0)}ms`);
      }

      setFlowErrors(errors);
      return { scratchpad: response, errors };
    },
    [flowService, setFlowErrors, saveFlow]
  );

  const flowRef = useCreateFlowRef({
    flow,
    onFlowChange: save,
    componentRegistry: {
      ApplicationSettingsForm: CutApplicationSettingsBaseFormTabbed,
      FlowActionBarEditActions: CuttingEditActions,
      FlowItem: CuttingFlowItemComponent,
      FlowItemEdit: CuttingFlowItemEdit,
      FlowItemMultiEdit: CuttingFlowItemMultiEdit,
      TeachDetail: CuttingTeachDetail,
      FlowSections: FlowSections,
    },
  });

  const isReadOnly = useMemo(() => {
    return (
      !canUpdateScratchpad ||
      !scratchpad?.active ||
      !isMachineActive ||
      !canUpdateAllBeaconCuttingFields
    );
  }, [
    canUpdateScratchpad,
    canUpdateAllBeaconCuttingFields,
    isMachineActive,
    scratchpad?.active,
  ]);

  let secondaryToolbar;

  if (flowRef) {
    secondaryToolbar = (
      <FlowActionBar
        flow={flow}
        flowRef={flowRef}
        flowValidationErrors={flowErrors}
        readOnly={isReadOnly}
        scratchpadId={scratchpad?.id}
      />
    );
  }

  const hasFlowErrors = (flowErrors?.length ?? 0) > 0;

  const presentActions = useCallback(() => {
    actionButtonRef.current?.present();
  }, []);

  const applicationSettingsModalRef = useRef<ApplicationSettingsModalRef>(null);

  const showApplicationSettings = useCallback(async () => {
    if (applicationSettingsModalRef.current) {
      return applicationSettingsModalRef.current?.showApplicationSettings();
    }

    return false;
  }, []);

  const backgroundColor =
    flow.config.stepModeEnabled === true
      ? 'var(--alt-color-header)'
      : undefined;

  return (
    <BackButtonProvider>
      <IonPage ref={pageRef}>
        <NestedPageHeader
          backgroundColor={backgroundColor}
          defaultBackRoute={builder.getMachineRoute(teamId, machineId)}
          secondaryToolbar={secondaryToolbar}
          rightButtons={
            <ActionButton
              ref={actionButtonRef}
              icon={
                <IonIcon icon={ellipsisVertical} style={{ fontSize: '24px' }} />
              }
            >
              {({ close }) => (
                <IonList lines="none">
                  <IonItem
                    button={true}
                    onClick={() => {
                      onModalChange(FlowModal.PART_DETAILS);
                    }}
                    disabled={hasFlowErrors}
                  >
                    <IonIcon slot="start" icon={informationCircleOutline} />
                    <IonLabel>
                      {getLocalizedText('common:part_details')}
                    </IonLabel>
                  </IonItem>
                  <IonItem
                    button={true}
                    onClick={() => {
                      onModalChange(FlowModal.APPLICATION_SETTINGS);
                    }}
                    disabled={hasFlowErrors}
                  >
                    <IonIcon slot="start" icon={welderKeyOn} />
                    <IonLabel>
                      {getLocalizedText('cutting:cut_settings')}
                    </IonLabel>
                  </IonItem>
                  <IonItem
                    button={true}
                    onClick={() => {
                      onModalChange(FlowModal.PART_HISTORY);
                    }}
                  >
                    <IonIcon slot="start" icon={timeOutline} />
                    <IonLabel>
                      {getLocalizedText('common:part_history')}
                    </IonLabel>
                  </IonItem>
                  <ScratchpadPlaylistOptions onAction={close} />
                </IonList>
              )}
            </ActionButton>
          }
        >
          <AppPageTitle title={scratchpad?.name ?? ''} />
        </NestedPageHeader>
        <IonContent>
          <FlowRefContext.Provider value={flowRef}>
            <FlowComponent
              flow={flow}
              flowRef={flowRef}
              flowValidationErrors={flowErrors}
              readOnly={isReadOnly}
              presentActions={presentActions}
              showApplicationSettings={showApplicationSettings}
            />
            <ScratchpadHistoryModal
              isOpen={activeModal === FlowModal.PART_HISTORY}
              onDidDismiss={() => setActiveModal(undefined)}
              presentingEl={pageRef.current}
            />
            <CuttingPartDetailModal
              isOpen={activeModal === FlowModal.PART_DETAILS}
              onDismiss={() => setActiveModal(undefined)}
              presentingEl={pageRef.current}
              readOnly={isReadOnly}
            />
            <ApplicationSettingsModalProvider
              isOpen={activeModal === FlowModal.APPLICATION_SETTINGS}
              onDismiss={() => setActiveModal(undefined)}
              readOnly={isReadOnly}
              presentingEl={pageRef.current}
              ref={applicationSettingsModalRef}
              defaultBucketTags={{
                cutter: flowService.getEquipmentName(),
              }}
              type={ScratchpadBucketType.CUT_SETTINGS}
            />
          </FlowRefContext.Provider>
        </IonContent>
      </IonPage>
    </BackButtonProvider>
  );
};

export { BeaconCuttingPage };
